import React, { useEffect, useRef, useState } from "react";

/**
 * HeroFX — premium hero overlay
 *   • Animated AI-grid (CSS conic + mask)
 *   • 12 floating orange/azure nodes (CSS-only keyframes)
 *   • Mouse-follow parallax spotlight (≥ md only, disabled if prefers-reduced-motion)
 *
 * Performance:
 *   - All animations are GPU-cheap (transform, opacity, background-position)
 *   - prefers-reduced-motion fully respected
 *   - Mobile (<768px) skips the parallax listener
 */
export const HeroFX = () => {
  const ref = useRef(null);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(m.matches);
    const onPref = () => setReduced(m.matches);
    m.addEventListener?.("change", onPref);

    if (m.matches || window.innerWidth < 768) {
      return () => m.removeEventListener?.("change", onPref);
    }

    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const onMove = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const x = (e.clientX / window.innerWidth - 0.5) * 30;
        const y = (e.clientY / window.innerHeight - 0.5) * 30;
        el.style.setProperty("--mx", `${x}px`);
        el.style.setProperty("--my", `${y}px`);
        raf = 0;
      });
    };
    window.addEventListener("mousemove", onMove);
    return () => {
      window.removeEventListener("mousemove", onMove);
      m.removeEventListener?.("change", onPref);
    };
  }, []);

  return (
    <div ref={ref} aria-hidden className="hero-fx absolute inset-0 pointer-events-none">
      {/* Animated grid */}
      <div className="hero-fx-grid absolute inset-0" />
      {/* Orange mouse spotlight (above grid) */}
      {!reduced && <div className="hero-fx-spotlight absolute inset-0" />}
      {/* Floating particles */}
      {!reduced && (
        <ul className="hero-fx-particles absolute inset-0 list-none m-0 p-0">
          {Array.from({ length: 14 }).map((_, i) => (
            <li key={i} style={{ "--i": i }} />
          ))}
        </ul>
      )}
    </div>
  );
};
